import { FILTERED_GC_CODES, GIFT_CARD_SERVICE_HOST_LOCALE_MAP } from "@/shared/constants";
import {
    ERROR_CODES,
    isPromotionErrorCode,
    normalizeErrorCode,
    normalizeOrderStatus,
    ORDER_STATUSES,
    POLLING_STATUS_CODES,
    PROMOTION_ERROR_CODES,
    SUCCESS_STATUS_CODES,
} from "@/shared/gift-cards.constants";

import { axios } from "@/utils/axios";
import logger from "@/utils/logger";
import { throwOrReturnDefault } from "@/utils/requests";
import { isSafePathSegment } from "@/utils/urlHelper";

import {
    Checkout,
    APIGiftCardCheckout,
    APIGiftCardOrderConfirmation,
    GiftCardProps,
    GiftCardResponse,
    APIOrderConfirmation,
    APIOrderStatus,
    OrderStatus,
} from "@/interfaces/gift-cards/gift-card-interface";
import {
    transformGiftCard,
    transformOrderStatus,
} from "@/interfaces/gift-cards/gift-card-transform";

import { Locale } from "@/types/locale";

export const buildGiftCardServiceOrdersBaseUrl = (locale: Locale): string => {
    return `${GIFT_CARD_SERVICE_HOST_LOCALE_MAP[locale]}/external/api/v1/gift-card-orders`;
};

export const buildGiftCardServiceGiftCardsBaseUrl = (locale: Locale): string => {
    return `${GIFT_CARD_SERVICE_HOST_LOCALE_MAP[locale]}/external/api/v1/gift-cards`;
};

export const fetchGiftCards = async (locale: Locale): Promise<Array<GiftCardProps>> => {
    try {
        const giftCardsResponse = await axios(
            `${buildGiftCardServiceGiftCardsBaseUrl(locale)}?locale=${locale}`
        );
        const giftCards = giftCardsResponse.data;
        //  the code is "WALMART" instead of "WALMART_USD"
        const cardsToFilter = FILTERED_GC_CODES; // @TODO temporarily filter cards by request of product

        const filteredGiftCards = giftCards?.data
            .filter((card) => !cardsToFilter.includes(card?.attributes?.sku))
            // Filter Blackhawk gift cards, identified by the _BHN code suffix (e.g. MACY_S_EGIFT_P1X0G4_BHN)
            .filter((card) => !/_BHN$/.test(card?.attributes?.code ?? ""))
            .map(transformGiftCard);

        // return giftCards?.data?.map(transformGiftCard) ?? null; // @TODO temporarily filter cards by request of product
        return filteredGiftCards ?? null;
    } catch (ex) {
        // This is a fix for the staging, where the gift cards service returns 500 with empty list message. Should not be a block error for rendering the page.
        if (ex?.response?.status === 500 && ex?.response?.data?.message === "List is empty.") {
            return [];
        }
        const errorDescription = `Failed to fetch gift cards locale=${locale}`;
        logger.logException(ex, errorDescription);
        return throwOrReturnDefault(ex);
    }
};

export const giftCardCheckout = async (
    checkout: APIGiftCardCheckout,
    locale: Locale
): Promise<GiftCardResponse<Checkout>> => {
    const checkoutEndpoint = `${buildGiftCardServiceOrdersBaseUrl(
        locale
    )}/external/api/v1/gift-card-orders/afterpay-checkout?locale=${locale}`;
    const checkoutResponse = await axios.post(checkoutEndpoint, { data: checkout });
    return checkoutResponse.data;
};

export const giftCardOrderConfirmation = async (
    token: string,
    giftCardCheckout: APIGiftCardCheckout,
    locale: Locale,
    hasAfterpayFinanceFee?: boolean
): Promise<GiftCardResponse<APIOrderConfirmation>> => {
    const data: APIGiftCardOrderConfirmation = {
        order_token: token,
        gift_card_id: giftCardCheckout.gift_card_id,
        is_gift: giftCardCheckout.is_gift,
        currency: giftCardCheckout.currency,
        consumer_firstname: giftCardCheckout.consumer_firstname,
        consumer_lastname: giftCardCheckout.consumer_lastname,
        consumer_email: giftCardCheckout.consumer_email,
        recipient_name: giftCardCheckout.recipient_name,
        recipient_email: giftCardCheckout.recipient_email,
        recipient_message: giftCardCheckout.recipient_message,
        has_afterpay_finance_fee: hasAfterpayFinanceFee,
    };
    const orderConfirmationEndpoint = `${buildGiftCardServiceOrdersBaseUrl(locale)}/process`;

    const orderConfirmationResponse = await axios.post(orderConfirmationEndpoint, { data });
    return orderConfirmationResponse.data;
};

/*
 * Function that requests the order status from the backend. Used when we poll for the order status.
 */
export const giftCardOrderStatus = async (
    orderId: number,
    locale: Locale
): Promise<GiftCardResponse<APIOrderStatus>> => {
    const orderStatusEndpoint = `${buildGiftCardServiceOrdersBaseUrl(locale)}/${orderId}/status`;

    const orderStatusResponse = await axios(orderStatusEndpoint);
    return orderStatusResponse.data;
};

/*
 * Thrown when the gift card service returns a valid response that reports the
 * order has legitimately failed (a terminal error_code or a fulfil_error
 * status). This is distinct from a transport/network failure — callers use
 * this distinction to decide whether to surface an error to the customer
 * (legitimate failure) or keep polling (transient blip). See getOrderStatus.
 */
export class OrderFulfillmentError extends Error {
    readonly errorCode: string | null;
    readonly orderStatus: string;

    constructor(errorCode: string | null, orderStatus: string) {
        super(`Order failed with code ${errorCode} and status ${orderStatus}`);
        this.name = "OrderFulfillmentError";
        this.errorCode = errorCode;
        this.orderStatus = orderStatus;
        // Keep `instanceof` reliable when Error subclasses are down-levelled (e.g. ts-jest
        // with an ES5 target rebinds the prototype to Error). The poll loop depends on it.
        Object.setPrototypeOf(this, OrderFulfillmentError.prototype);
    }

    /*
     * The order was cancelled because its promotional offer could not be applied
     * (offer no longer available, or already claimed by this customer). Unlike
     * other fulfillment failures the customer can simply re-order at regular price.
     */
    get isPromotionError(): boolean {
        return isPromotionErrorCode(this.errorCode);
    }
}

export const isPromotionFailure = (err: unknown): err is OrderFulfillmentError =>
    err instanceof OrderFulfillmentError && err.isPromotionError;

type ProcessFailure = { response?: { data?: { error?: { promo_error?: string | null } } } };

/*
 * /process refuses a promotional order before payment auth with an `error.promo_error`
 * code (e.g. PROMOTION_ALREADY_CLAIMED). Nothing was charged and there is no order to
 * poll, so surface it as the same terminal promotion failure the status poll reports;
 * an unrecognised promotion code is treated as PROMOTION_UNAVAILABLE. Returns null for
 * every other failure.
 */
export const promotionFailureFromProcessError = (err: unknown): OrderFulfillmentError | null => {
    const errorCode = normalizeErrorCode(
        (err as ProcessFailure)?.response?.data?.error?.promo_error
    );
    if (!errorCode) {
        return null;
    }
    return new OrderFulfillmentError(
        isPromotionErrorCode(errorCode) ? errorCode : PROMOTION_ERROR_CODES.UNAVAILABLE,
        ORDER_STATUSES.FULFILLMENT_ERROR
    );
};

export const getOrderStatus = (orderId: number, locale: Locale): Promise<OrderStatus> => {
    return new Promise((resolve, reject) => {
        giftCardOrderStatus(orderId, locale)
            .then(({ data }) => {
                // Case-insensitive: see normalizeOrderStatus / normalizeErrorCode.
                const errorCode = normalizeErrorCode(data.error_code);
                const orderStatus = normalizeOrderStatus(data.order_status);

                if (
                    ERROR_CODES.includes(errorCode) ||
                    orderStatus === ORDER_STATUSES.FULFILLMENT_ERROR
                ) {
                    // Legitimate, terminal order failure reported by the service.
                    return reject(new OrderFulfillmentError(errorCode, orderStatus));
                }
                if (
                    !POLLING_STATUS_CODES.includes(orderStatus) &&
                    !SUCCESS_STATUS_CODES.includes(orderStatus)
                ) {
                    // Neither in flight nor an explicit success (e.g. a 200 with an empty
                    // `data`). Not proof the order succeeded, so reject as transient and let
                    // the caller keep polling rather than confirm it.
                    return reject(new Error(`Unexpected order status: ${data.order_status}`));
                }
                resolve(transformOrderStatus(data));
            })
            // Transport/network failure (unreachable service, timeout, 4xx/5xx).
            // Kept as a generic Error so callers can treat it as transient.
            .catch((err) => reject(new Error(`Order status check failed: ${err}`)));
    });
};

export async function fetchGiftCard(
    giftCardCode: string,
    locale: Locale
): Promise<GiftCardProps | undefined> {
    if (!isSafePathSegment(giftCardCode)) {
        return undefined;
    }

    const url = `${buildGiftCardServiceGiftCardsBaseUrl(locale)}/${encodeURIComponent(
        giftCardCode
    )}?locale=${locale}&activeOnly=true`;
    try {
        const response = await axios(url);
        const { data } = response.data;
        if (data !== null) {
            return transformGiftCard(data);
        }
    } catch (ex) {
        const errorDescription = `failed to fetch gift card giftCardCode=${giftCardCode} locale=${locale} url=${url}`;
        logger.logWarn(ex, errorDescription);
        if (ex?.response?.status !== 404) {
            throw ex;
        }
    }
}
